<template lang="pug">
.Summary
  nav.SummaryBar
    .flex.justify-end
      .SummaryBarElement.Price.Per-Book(v-if='showPrices && !isTotalInvalid')
        .price {{ oneBookPrice | price }}

        .description Per book

      .SummaryBarElement.Price(v-if='showPrices')
        .price.price--invalid(v-if='isTotalInvalid') Calculating...
        .price(v-else) {{ totalPrice | price }}
        .description Total ({{this.currency}})

      .SummaryBarElement.Currency(v-if='showPrices && !this.settings.usdConversionRate==0')
        span(
          @click='toggleCurrency("USD")',
          :class=`{ current: this.settings.currency=="USD" }`,
        ) USD
        span &nbsp;|&nbsp;
        span(
          @click='toggleCurrency("CAD")',
          :class=`{ current: this.settings.currency=="CAD" }`,
        ) CAD

      .SummaryBarElement.Toggler(
        @click='togglePanel',
        :class=`{ active: panelOpened }`,
        ref='button'
      )
        include ./up.svg
        span Summary

  transition( name='slide-up' )
    section.SummaryPanel(
      v-show='panelOpened'
      ref='panel'
    )
      h3.SectionName Summary

      table( cellspacing="0" )
        tr(
          v-for='(categorySummary, catI) in categoriesSummaries',
          v-if='!isCategoryIndexDisabled[catI]'
        )
          td
            p.b {{ categorySummary.category }}

          td
            .dark-red(v-if='invalidCategoriesIndexes.includes(catI)') Unavailable
            span(v-else) {{ categorySummary.name | ratio }}

        tr
          td.b.pb3 Pages per book
          td {{ quantities.pages }}

        tr
          td.b.pb3 Number of books
          td {{ quantities.books }}

      hr(v-if="showPrices")

      div( v-if='discountPrice > 0' )
        table( cellspacing="0" )
          tr
            td.b.pb3 Pre-total
            td
              .dark-red(v-if='isTotalInvalid') ?
              span(v-else) {{ preTotalPrice | price }}

        table( cellspacing="0" )
          tr
            td.b.pb3 Quantity discount
            td
              .dark-red(v-if='isTotalInvalid') ?
              span(v-else) - {{ discountPrice | price }}

      table( cellspacing="0" v-if="showPrices")
        tr
          td.b.pb2 Total per book
          td
            .dark-red(v-if='isTotalInvalid') ?
            span(v-else) {{ oneBookPrice | price }} {{currency}}

        tr
          td.b.pb2 Total
          td.b
            .dark-red(v-if='isTotalInvalid') ?
            span(v-else) {{ totalPrice | price }} {{currency}}
</template>

<script>
import numeral from 'numeral'

export default {
  name: 'bookart-summary',

  props: ['categories', 'disabledOptions', 'discounts', 'pageprices', 'groups', 'quantities', 'selectedOptions', 'settings'],

  data() {
    var currency = 'USD'
    if (localStorage.currency == undefined || localStorage.currency == '') {
      currency = 'USD'
    } else {
      currency = localStorage.currency
    }

    this.settings.currency = currency

    localStorage.currency = currency

    if (this.settings.usdConversionRate != 0) currency = localStorage.currency
    return {
      panelOpened: false,
      currency: currency
    }
  },

  filters: {
    ratio(val) {
      return val.replace(/([0-9]+)x([0-9]+)/gi, '$1 × $2')
    },
    price(val) {
      return numeral(val).format('$ 0,0.00')
    }
  },

  computed: {
    showPrices() {
      return this.settings.showPrices
    },
    exRate() {
      if (this.currency == 'USD') var exRate = this.settings.usdConversionRate
      else var exRate = 1

      return exRate
    },

    coverPrice() {
      if (window.DEBUG) {
        console.groupCollapsed('Cover price')
      }

      const coverPrice = this.groups.cover.reduce((coverPrice, catI) => {
        const name = this.categories[catI].name

        if (this.isCategoryIndexDisabled[catI]) {
          if (window.DEBUG) console.log(`Category ${name} is disabled, skipping.`)
          return coverPrice
        } else {
          const option = this.categories[catI].options[this.selectedOptions[catI]]
          if (window.DEBUG) console.log(`Category ${name} has option ${option.name} selected. Adding ${option.price}$ to total.`)
          return coverPrice + option.price
        }
      }, 0)

      if (window.DEBUG) {
        console.log(`Total cover price: ${coverPrice}$`)
        console.groupEnd('Cover price')
      }

      return coverPrice
    },

    setupFee() {
      var setupFees = ['Cover Lamination', 'Printing', 'Binding Type']
      var i = 0
      var setupFee = 0
      for (i = 0; i < setupFees.length; i++) {
        const catI = this.categories.findIndex(cat => cat.name === setupFees[i])
        const option = this.categories[catI].options[this.selectedOptions[catI]]
        setupFee = setupFee + option.setupFee
      }
      return setupFee
    },

    printPrice() {
      // Find the pageprice in which the current page quantity fits
      const pagepriceRange = this.pageprices.find(
        pageprice =>
          this.quantities.books * this.quantities.pages >= pageprice.min &&
          this.quantities.books * this.quantities.pages <= pageprice.max
      )
      const catI = 9
      const printType = this.selectedOptions[catI]
      const cat = this.categories
      const opt = cat[9].options
      const printOpt = opt[printType]
      const printId = printOpt.id
      const price = pagepriceRange.distribution[printId]
      return price
    },

    pagePrice() {
      if (window.DEBUG) {
        console.groupCollapsed('Page price')
      }

      const pagePrice = this.groups.page.reduce((pagePrice, catI) => {
        const name = this.categories[catI].name

        if (this.isCategoryIndexDisabled[catI]) {
          if (window.DEBUG) console.log(`Category ${name} is disabled, skipping.`)
          return pagePrice
        } else {
          const option = this.categories[catI].options[this.selectedOptions[catI]]
          if (window.DEBUG) console.log(`Category ${name} has option ${option.name} selected. Adding ${option.price}$ to total.`)
          return pagePrice + option.price + this.printPrice
        }
      }, 0)

      if (window.DEBUG) {
        console.log(`Total page price: ${pagePrice}$`)
        console.groupEnd('Page price')
      }

      return pagePrice
    },

    bookSizeMultiplier() {
      const catI = this.groups.multiplier[0]
      return this.categories[catI].options[this.selectedOptions[catI]].price
    },

    proofingPrice() {
      const catI = this.groups.proofing[0]
      return this.categories[catI].options[this.selectedOptions[catI]].price
    },

    stampingFlatFee() {
      const catI = this.categories.findIndex(cat => cat.name === 'Stamping')
      if (this.categories[catI].options[this.selectedOptions[catI]].price > 0) {
        return this.settings.stampingFlatFee
      } else {
        return 0
      }
    },

    stampingPerBookFee() {
      const catI = this.categories.findIndex(cat => cat.name === 'Stamping')
      if (this.categories[catI].options[this.selectedOptions[catI]].price > 0) {
        return this.settings.stampingPerBookFee * this.categories[catI].options[this.selectedOptions[catI]].price
      } else {
        return 0
      }
    },

    discountPrice() {
      // Find the discount in which the current book quantity fits
      const discountRange = this.discounts.find(
        discount => this.quantities.books >= discount.min && this.quantities.books <= discount.max
      )

      // If there's none, return a 0$ discount
      if (!discountRange) return 0

      if (window.DEBUG) {
        console.groupCollapsed('Discount')
        console.log(`Book quantity matched discount range [${discountRange.min}..${discountRange.max}]`)
      }

      const discountPrices = ['cover', 'page', 'proofing'].reduce((discountPrices, group) => {
        discountPrices[group] = this.groups[group].reduce((coverPrice, catI) => {
          if (this.isCategoryIndexDisabled[catI]) return coverPrice

          const cat = this.categories[catI]
          const opt = cat.options[this.selectedOptions[catI]]
          const percentDiscount = discountRange.distribution[cat.id] || 0
          const discount = (opt.price * percentDiscount * opt.discount) / 100

          if (window.DEBUG && discount > 0) {
            console.log(
              `Applying ${percentDiscount}% discount on ${cat.name}'s selected option "${opt.name}"'s price ${opt.price.toFixed(
                2
              )}$ for a total of ${discount.toFixed(2)}$ discount`
            )
          }

          if (window.DEBUG ** percentDiscount < 0) {
            console.error(`ERROR: USING NEGATIVE PERCENTAGES PRODUCES WRONG RESULTS`)
          }

          return coverPrice + discount
        }, 0)

        if (window.DEBUG) {
          console.log(`Discount for ${group} price is ${discountPrices[group].toFixed(2)}$`)
        }

        return discountPrices
      }, {})

      const totalDiscount =
        discountPrices.proofing +
        this.bookSizeMultiplier *
          (this.quantities.books * discountPrices.cover + this.quantities.books * this.quantities.pages * discountPrices.page)

      if (window.DEBUG) {
        console.log(`Applying discount formula`)
        console.log(`Discount for Proofing price: ${discountPrices.proofing.toFixed(2)}$ +
        Book Size Multiplier: ${this.bookSizeMultiplier} × (
          (Book Quantity: ${this.quantities.books} × Discount for Cover Price: ${discountPrices.cover.toFixed(2)}$) +
          (Book Quantity: ${this.quantities.books} × Page Quantity: ${
          this.quantities.pages
        } × Discount for Page Price: ${discountPrices.page.toFixed(2)}$)
        )`)
        console.log(`Total discount: ${totalDiscount.toFixed(2)}$`)
        console.groupEnd()
      }

      return totalDiscount >= 0 ? totalDiscount : 0
    },

    oneBookPrice() {
      const flatFeesSplitPerBook = (this.proofingPrice + this.stampingFlatFee + this.setupFee) / this.quantities.books
      const perBookFees = this.stampingPerBookFee
      const discountPricePerBook = this.discountPrice / this.quantities.books
      const coverAndPagePricePerBook = this.coverPrice + this.quantities.pages * this.pagePrice
      return (
        (this.bookSizeMultiplier * coverAndPagePricePerBook - discountPricePerBook + flatFeesSplitPerBook + perBookFees) *
        this.exRate
      )
    },

    preTotalPrice() {
      return (
        this.proofingPrice +
        this.stampingFlatFee +
        this.setupFee +
        this.stampingPerBookFee * this.quantities.books +
        this.bookSizeMultiplier *
          (this.quantities.books * this.coverPrice + this.quantities.books * this.quantities.pages * this.pagePrice)
      )
    },

    totalPrice() {
      return (this.preTotalPrice - this.discountPrice) * this.exRate
    },

    // Helper that returns an array with a boolean for every category
    // indicating if it every of its options are disabled or not
    isCategoryIndexDisabled() {
      return this.categories.map(cat => {
        return cat.options.every(opt => this.disabledOptions.includes(opt.id))
      })
    },

    invalidCategoriesIndexes() {
      return this.categories.reduce((invalidCategoriesIndexes, cat, catI) => {
        const isCategoryDisabled = this.isCategoryIndexDisabled[catI]
        const selectedOptionId = cat.options[this.selectedOptions[catI]].id
        const isSelectedOptionDisabled = this.disabledOptions.includes(selectedOptionId)

        if (!isCategoryDisabled && isSelectedOptionDisabled) {
          invalidCategoriesIndexes.push(catI)
        }
        return invalidCategoriesIndexes
      }, [])
    },

    isTotalInvalid() {
      return this.invalidCategoriesIndexes.length > 0
    },

    // Used for displaying categories
    categoriesSummaries() {
      return this.categories.map((cat, catI) => {
        const option = cat.options[this.selectedOptions[catI]]

        return {
          category: cat.name,
          name: option.customField ? `${option.customFieldData} (Custom)` : option.name,
          price: option.price
        }
      })
    }
  },

  methods: {
    closeOnOutsideClick(e) {
      // If the click originated from inside of any of panel
      // triggering elements, abort closing
      if (this.$refs.button.contains(e.target)) return
      if (this.$refs.panel.contains(e.target)) return

      this.closePanel()
    },

    closePanel() {
      this.panelOpened = false
      document.removeEventListener('click', this.closeOnOutsideClick)
    },

    openPanel() {
      this.panelOpened = true
      document.addEventListener('click', this.closeOnOutsideClick)
    },

    togglePanel() {
      this[this.panelOpened ? 'closePanel' : 'openPanel']()
    },

    toggleCurrency(currency) {
      this.currency = currency
      localStorage.currency = currency
      this.settings.currency = currency
    }
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeOnOutsideClick)
  }
}
</script>

<style lang="stylus" scoped>

@import '../../styles/vars'

$background-color = $white-almost
$border-type = dashed

.slide-up-enter-active
  transition: all .2s ease-out

.slide-up-leave-active
  transition: all .3s ease-in

.slide-up-enter, .slide-up-leave-active
  transform: translateY(100%)
  opacity: 0

.SummaryBar
  z-index: $z-top
  position: fixed
  right: 0

  background: $background-color
  border-top: 1px $border-type
  border-left: 1px $border-type
  border-color: $black
  cursor: default

  width: 100%
  height: $summary-bar-height
  line-height: $summary-bar-height
  bottom: $steps-bar-collapsed-height
  border-bottom: 1px solid $gray-dark

  @media $breakpoints.ns
    width: auto
    height: $summary-bar-height-ns
    line-height: $summary-bar-height-ns
    border-bottom: 0
    bottom: 0

.SummaryBarElement
  position: relative
  top: -1px

  padding: 0 .5rem
  text-align: center

  border-color: $black
  border-top: 1px $border-type transparent
  border-right: 1px $border-type

  width: 50%
  @media $breakpoints.ns
    padding: 0 2rem
    width: auto

  &:last-child
    border-right: none

  &.Per-Book
    display: none
    @media $breakpoints.ns
      display: initial

  &.Currency
    .current
      padding-bottom:3px;
      border-bottom:2px solid #000;

  &.Price
    line-height: 17px
    text-align: center
    font-weight: 400
    font-size: 0.8em

    padding-top: 10px
    @media $breakpoints.ns
      padding-top: 15px

    .price
      color: $green

      &.price--invalid
        color: $red

    .description
      text-transform: uppercase
      font-weight: 700
      font-size: 9px

  &.Toggler
    cursor: pointer
    font-size: 0.8em
    text-transform: uppercase

    @media $breakpoints.ns
      font-size: 1em

    svg
      position: relative
      top: -3px
      margin-right: .5rem

      @media $breakpoints.ns
        margin-right: 1rem

      transform: rotate(0)
      transition: transform ease-out .2s

    &.active
      border-top: 1px solid white

      svg
        transform: rotate(180deg)

.SectionName
  margin-top: 0
  margin-bottom: 2rem
  border-bottom: 2px solid $black
  padding-bottom: 1rem

.SummaryPanel
  z-index: $z-top

  position: fixed
  top: -1px
  right: 0
  bottom: $steps-bar-collapsed-height + $summary-bar-height
  width: 500px
  max-width: 100vw
  background: $background-color

  @media $breakpoints.ns
    bottom: $summary-bar-height-ns

  border-top: 1px $border-type
  border-left: 1px $border-type
  border-bottom: 1px $border-type
  border-color: $black

  font-size: 14px
  padding: 2rem
  overflow-x: hidden
  overflow-y: auto
  -webkit-overflow-scrolling: touch

table
  width: 100%

  tr
    display: flex
    padding-bottom: .5rem
    justify-content: space-between;

    &:last-child
      padding-bottom: 0

  td:last-child
    text-align: right
    vertical-align: top
    white-space: nowrap

  td p
    margin: 0

    &:last-child
      margin-bottom: 1rem

hr
  border: 1px solid black
  margin: 0 0 1rem 0
</style>



// WEBPACK FOOTER //
// BookartSummary.vue?5e0282d4